import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { ArrowRight } from "components/icons/ArrowRight";
import { WaveLines } from "components/icons/WaveLines";
import { WaveLinesBottom } from "components/icons/WaveLinesBottom";
import { INKLE_AI_ONBOARDING_COMPLETED } from "constants/analyticsEvents";
import { AI_ONBOARDING_STEP_KEY, ONBOARDING_STATUS } from "constants/inkleAI";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  useCreateAIChannelMutation,
  useUpdateOnBoardingMutation,
} from "store/apis/inkleAI";
import InkleAiIcon from "../../static/images/InkleAIIcon.png";

type InkleAISliderProps = {
  open: boolean;
  onClose: () => void;
  chatSliderOpen: () => void;
  uuid: string;
};

export const InkleAIOnboardingSlider = ({
  open,
  onClose,
  chatSliderOpen,
  uuid,
}: InkleAISliderProps) => {
  const [updateOnBoarding, { isLoading: isUpdating }] =
    useUpdateOnBoardingMutation();
  const [createAIChannel, { isLoading: isCreating }] =
    useCreateAIChannelMutation();
  const { uuid: groupId, ai_channel_url } = useCurrentGroupContext();
  const { trackEvent } = useAnalytics();

  const isLoading = isUpdating || isCreating;

  const handleGetStarted = async () => {
    if (ai_channel_url) {
      onClose();
      chatSliderOpen();
      return;
    }

    trackEvent(INKLE_AI_ONBOARDING_COMPLETED);

    try {
      await updateOnBoarding({
        uuid,
        step_key: AI_ONBOARDING_STEP_KEY,
        status: ONBOARDING_STATUS.COMPLETED,
      }).unwrap();

      await createAIChannel({
        groupId,
        step_key: AI_ONBOARDING_STEP_KEY,
        status: ONBOARDING_STATUS.NOT_STARTED,
      }).unwrap();

      onClose();
      chatSliderOpen();
    } catch (error) {
      console.error("Failed to start AI onboarding:", error);
    }
  };
  return (
    <>
      <Slider.Root open={open} onOpenChange={onClose}>
        <Slider.Content open={open} useCustomOverlay width={800}>
          <div className="t-relative t-h-full">
            <div className="t-absolute t-inset-0 t-overflow-hidden t-pointer-events-none">
              <WaveLines />
            </div>
            <div className="t-flex t-justify-end t-p-2">
              <Slider.Close />
            </div>
            <div className="t-relative ">
              <Slider.Body>
                <div className="t-flex t-flex-col t-items-center t-justify-center t-min-h-[calc(100vh-100px)] t-px-8">
                  <div className="t-mb-6">
                    <img
                      className="t-w-16 t-h-16"
                      src={InkleAiIcon}
                      alt="Inkle AI Icon"
                    />
                  </div>
                  <div className="t-flex t-flex-row t-items-center t-justify-center t-gap-2">
                    <span className="t-text-center t-text-[20px] t-font-medium t-leading-6 t-bg-gradient-to-r t-from-[#FB6FD5] t-via-[#C65DE7] t-to-[#8A4ED4] t-bg-clip-text t-text-transparent">
                      Welcome to Inkle AI!
                    </span>

                    <Tag rounded icon={false} tagType="blue" size="small">
                      Beta
                    </Tag>
                  </div>

                  <p className="t-mb-8 t-text-center t-max-w-md t-mt-4 t-text-[#706A85] t-text-sm t-font-normal t-leading-[22px]">
                    Your assistant for getting tasks done faster. Pick a task,
                    like Create bill from uploaded document, and Inkle AI will
                    take care of the rest.
                  </p>

                  <Button
                    size="regular"
                    customType="primary"
                    onClick={handleGetStarted}
                    disabled={isLoading}
                  >
                    <div className="t-flex t-items-center t-gap-2">
                      {isLoading ? "Starting..." : "Get started"}
                      {!isLoading && <ArrowRight color="white" />}
                    </div>
                  </Button>
                </div>
              </Slider.Body>
            </div>
          </div>
          <div className="t-absolute t-inset-0 t-overflow-hidden t-pointer-events-none">
            <WaveLinesBottom />
          </div>
        </Slider.Content>
      </Slider.Root>
    </>
  );
};
